import type { ComponentProps } from 'react'
import React, { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import RadioInput from 'LuxKit/Radio/RadioInput'
import useToggle from 'hooks/useToggle'
import Breakpoint from 'components/Common/Breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import type { OfferListSortOption } from 'constants/offerListFilters'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import Modal from 'LuxKit/Modal/Modal'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import Group from 'components/utils/Group'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { rem } from 'polished'
import FilterChip from 'LuxKit/Chips/FilterChip'
import { useAppSelector } from 'hooks/reduxHooks'

const RadioListItemsGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

const OptionsWrapper = styled.div`
  position: relative;
`

interface SortChangedParams {
  search: string
}

export type SortControlChangeHandler = ((params: SortChangedParams) => void)

interface Props {
  sortOptions: Array<OfferListSortOption>
  condenseOnMobile?: boolean
  onOpen?: () => void
  onSelect?: (option: OfferListSortOption) => void
  roundedButton?: boolean
  dropdownPlacement?: ComponentProps<typeof DropdownList>['placement']
  showDropdown?: boolean
  showSelectedHighlight?: boolean
  onSortChange: SortControlChangeHandler
}

function OfferSortControl({
  sortOptions,
  condenseOnMobile,
  onOpen,
  onSelect,
  roundedButton = false,
  dropdownPlacement,
  showDropdown = false,
  showSelectedHighlight = false,
  onSortChange,
}: Props) {
  const intl = useIntl()
  const { SORT_OPTION_RECOMMENDED } = useOfferListSortOptions()
  const { value: isOpenSort, toggle: toggleSort, off: closeSort } = useToggle()
  const windowSearch = useAppSelector(state => state.router.location.search)
  useEffect(() => {
    isOpenSort && onOpen?.()
  }, [isOpenSort, onOpen])

  const searchParams = parseSearchString(windowSearch)

  const currentOption = sortOptions.find(option => option.value === searchParams.sortBy) ?? SORT_OPTION_RECOMMENDED

  const onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    const value = e.currentTarget.value
    const newParams = new URLSearchParams(windowSearch)
    if (value === 'recommended') {
      // `recommended` is default. In this case, leave the param out entirely for consistency
      newParams.delete('sortBy')
    } else {
      newParams.set('sortBy', value)
    }

    onSortChange({ search: newParams.toString() })
    closeSort()
    onSelect?.(sortOptions.find(option => option.value === value)!)
  }, [windowSearch, onSortChange, closeSort, onSelect, sortOptions])

  const sortInputs = sortOptions.map(option => <RadioInput
    key={option.value}
    checked={currentOption.value === option.value}
    onChange={onChange}
    name={option.label}
    value={option.value}
  >
    {option.label}
  </RadioInput>)

  const triggerRef = useRef<HTMLButtonElement>(null)

  const sortLabel = intl.formatMessage({
    id: 'kiCwNcQ+qJ',
    defaultMessage: 'Sort',
    description: 'Mobile sort button label for offer list on the offer listing or detail page',
  })
  const sortWithOptionLabel = intl.formatMessage({
    id: 'KQItyfXIPA',
    defaultMessage: 'Sort: {option}',
    description: 'Desktop sort button showing current sort option on the form',
  }, { option: currentOption.label })

  const buttonContent = condenseOnMobile ? <>
    <CSSBreakpoint max="mobile" as="span">{sortLabel}</CSSBreakpoint>
    <CSSBreakpoint min="tablet" as="span">{sortWithOptionLabel}</CSSBreakpoint>
  </> : <>
    {sortWithOptionLabel}
  </>

  const selected = showSelectedHighlight && currentOption.value !== 'recommended'

  return (
    <OptionsWrapper>
      {roundedButton && <FilterChip
        selected={selected}
        size="medium"
        onClick={toggleSort}
        endIcon={isOpenSort ? <LineAngleUpIcon /> : <DropdownChevron />}
        ref={triggerRef}
      >
        {buttonContent}
      </FilterChip>}
      {!roundedButton && <TextButton kind="secondary" variant="dark" onClick={toggleSort} endIcon={isOpenSort ? <LineAngleUpIcon /> : <DropdownChevron />} ref={triggerRef}>
        {buttonContent}
      </TextButton>}
      <Breakpoint min={showDropdown ? undefined : 'tablet'}>
        <DropdownList size="S" open={isOpenSort} onClose={closeSort} anchorRef={triggerRef} triggerRef={triggerRef} placement={dropdownPlacement}>
          <RadioListItemsGroup direction="vertical" gap={16}>
            {sortInputs}
          </RadioListItemsGroup>
        </DropdownList>
      </Breakpoint>
      {!showDropdown && <Breakpoint max="mobile">
        <Modal
          title={intl.formatMessage({
            id: 'yst+fWuhSv',
            defaultMessage: 'Sort by',
            description: 'Title for mobile sort modal in offer list on the offer page',
          })}
          onClose={closeSort}
          isOpen={isOpenSort}
          primaryActionText={intl.formatMessage({
            id: 'U1kaMpi7yL',
            defaultMessage: 'Apply',
            description: 'Action button to apply selected sort option in modal',
          })}
          onPrimaryActionClick={closeSort}
        >
          <RadioListItemsGroup direction="vertical" gap={16}>
            {sortInputs}
          </RadioListItemsGroup>
        </Modal>
      </Breakpoint>}
    </OptionsWrapper>
  )
}

export default OfferSortControl
